import React, { useEffect, useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Divider
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

import { getOpeningHoursAll, deleteOpeningHour } from "../../../lib/apiClientOpeningHours";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { useTranslation } from "react-i18next";

// Helper to convert day string to weekday index
const weekDayStringToIndex = (day) => {
    switch (day) {
        case "Sunday": return 0;
        case "Monday": return 1;
        case "Tuesday": return 2;
        case "Wednesday": return 3;
        case "Thursday": return 4;
        case "Friday": return 5;
        case "Saturday": return 6;
        default: return -1;
    }
};

const DeleteOpeningHoursDialog = ({ open, onClose, businessId }) => {

    const { t } = useTranslation("deleteOpeningHoursDialog");

    // Weekday translation from JSON files
    const weekdayNames = [
        t("weekdayNames.0"),
        t("weekdayNames.1"),
        t("weekdayNames.2"),
        t("weekdayNames.3"),
        t("weekdayNames.4"),
        t("weekdayNames.5"),
        t("weekdayNames.6")
    ];

    const [grouped, setGrouped] = useState([]);
    const [deleteId, setDeleteId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const loadHours = useCallback(async () => {
        try {
            const data = await getOpeningHoursAll(businessId);

            const map = {};
            data.forEach(item => {
                const key = `${item.validFrom}__${item.validTo}`;
                if (!map[key]) {
                    map[key] = {
                        validFrom: item.validFrom,
                        validTo: item.validTo,
                        entries: []
                    };
                }
                map[key].entries.push(item);
            });

            setGrouped(Object.values(map));
        } catch (err) {
            console.error("Failed fetching opening hours", err);
        }
    }, [businessId]);

    useEffect(() => {
        if (open) loadHours();
    }, [open, loadHours]);

    const handleConfirmDelete = async () => {
        try {
            await deleteOpeningHour(deleteId);
            await loadHours();
        } catch (err) {
            console.error("Deletion failed", err);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "";
        const [y, m, d] = iso.split("-");
        return `${d}-${m}-${y}`;
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">

                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    {t("title")}
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent dividers>

                    {grouped.map((block, idx) => (
                        <Box key={idx} mb={3}>
                            <strong>
                                {formatDate(block.validFrom)} → {formatDate(block.validTo)}
                            </strong>

                            <List>
                                {block.entries.map(item => (
                                    <ListItem
                                        key={item.id}
                                        secondaryAction={
                                            <IconButton
                                                onClick={() => {
                                                    setDeleteId(item.id);
                                                    setConfirmOpen(true);
                                                }}
                                            >
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={`${weekdayNames[weekDayStringToIndex(item.weekDay)]}: ${item.openTime} - ${item.closeTime}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Divider sx={{ mt: 1 }} />
                        </Box>
                    ))}

                    {grouped.length === 0 && (
                        <Box textAlign="center" mt={2} color="gray">
                            {t("noData")}
                        </Box>
                    )}

                </DialogContent>
            </Dialog>

            <ConfirmDeleteDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
            />
        </>
    );
};

export default DeleteOpeningHoursDialog;